import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { EmptyState } from '../common/EmptyState';
import { Select } from '../common/Select';
import { Toggle } from '../common/Toggle';
import { useToast } from '../../contexts/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { apiClient } from '../../services/api';
import type {
  OnHoldReasonDefinitionDto,
  ReasonDefinitionDto,
  SystemReasonTemplateDto,
  UpsertSystemReasonTemplateRequest,
} from '../../types';

const MAX_REASONS = 20;
const NEW_TEMPLATE_KEY = '__new__';

type ReasonSubTab = 'resolve' | 'on-hold' | 'cancel';

type EditableReason = ReasonDefinitionDto;
type EditableOnHoldReason = OnHoldReasonDefinitionDto;

interface ReasonTemplateDraft {
  name: string;
  description: string;
  isDefault: boolean;
  resolveReasons: EditableReason[];
  onHoldReasons: EditableOnHoldReason[];
  cancelReasons: EditableReason[];
}

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function createLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function slugifyReasonValue(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function toEditableReason(reason: ReasonDefinitionDto): EditableReason {
  return {
    ...reason,
    id: reason.id || createLocalId('reason'),
    label: reason.label ?? '',
    value: reason.value ?? '',
    description: reason.description ?? '',
    isActive: reason.isActive !== false,
    sortOrder: Number.isFinite(reason.sortOrder) ? reason.sortOrder : 0,
  };
}

function toEditableOnHoldReason(reason: OnHoldReasonDefinitionDto): EditableOnHoldReason {
  return {
    ...reason,
    id: reason.id || createLocalId('on-hold-reason'),
    label: reason.label ?? '',
    value: reason.value ?? '',
    description: reason.description ?? '',
    isActive: reason.isActive !== false,
    sortOrder: Number.isFinite(reason.sortOrder) ? reason.sortOrder : 0,
    requiresFollowUp: Boolean(reason.requiresFollowUp),
  };
}

function toDraft(template: SystemReasonTemplateDto): ReasonTemplateDraft {
  return {
    name: template.name ?? '',
    description: template.description ?? '',
    isDefault: Boolean(template.isDefault),
    resolveReasons: asArray(template.resolveReasons).map(toEditableReason),
    onHoldReasons: asArray(template.onHoldReasons).map(toEditableOnHoldReason),
    cancelReasons: asArray(template.cancelReasons).map(toEditableReason),
  };
}

function createReason(sortOrder: number): EditableReason {
  return {
    id: createLocalId('reason'),
    label: '',
    value: '',
    description: '',
    isActive: true,
    sortOrder,
  };
}

function createOnHoldReason(sortOrder: number): EditableOnHoldReason {
  return {
    id: createLocalId('on-hold-reason'),
    label: '',
    value: '',
    description: '',
    isActive: true,
    sortOrder,
    requiresFollowUp: false,
  };
}

function createDefaultDraft(): ReasonTemplateDraft {
  return {
    name: '',
    description: '',
    isDefault: false,
    resolveReasons: [],
    onHoldReasons: [],
    cancelReasons: [],
  };
}

function toSortedReasons<T extends EditableReason | EditableOnHoldReason>(reasons: T[]): T[] {
  return [...asArray(reasons)]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((reason, index) => ({ ...reason, sortOrder: index }));
}

function moveReason<T extends EditableReason | EditableOnHoldReason>(reasons: T[], index: number, nextIndex: number): T[] {
  if (index < 0 || index >= reasons.length || nextIndex < 0 || nextIndex >= reasons.length) {
    return reasons;
  }

  const next = [...reasons];
  const [item] = next.splice(index, 1);
  next.splice(nextIndex, 0, item);
  return toSortedReasons(next);
}

function normalizeTemplate(rawTemplate: SystemReasonTemplateDto, index: number): SystemReasonTemplateDto {
  const safeTemplate = rawTemplate ?? ({} as SystemReasonTemplateDto);
  return {
    ...safeTemplate,
    id: typeof safeTemplate.id === 'string' && safeTemplate.id.trim() ? safeTemplate.id : `reason-template-${index + 1}`,
    name: typeof safeTemplate.name === 'string' && safeTemplate.name.trim() ? safeTemplate.name : `Reason Template ${index + 1}`,
    description: typeof safeTemplate.description === 'string' ? safeTemplate.description : '',
    isDefault: Boolean(safeTemplate.isDefault),
    resolveReasons: asArray(safeTemplate.resolveReasons).map(toEditableReason),
    onHoldReasons: asArray(safeTemplate.onHoldReasons).map(toEditableOnHoldReason),
    cancelReasons: asArray(safeTemplate.cancelReasons).map(toEditableReason),
    createdAt: typeof safeTemplate.createdAt === 'string' ? safeTemplate.createdAt : '',
    updatedAt: typeof safeTemplate.updatedAt === 'string' ? safeTemplate.updatedAt : undefined,
  };
}

export function ReasonConfigTab() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [activeSubTab, setActiveSubTab] = useState<ReasonSubTab>('resolve');
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [newTemplateDraft, setNewTemplateDraft] = useState<ReasonTemplateDraft | null>(null);
  const [draftsByTemplateId, setDraftsByTemplateId] = useState<Record<string, ReasonTemplateDraft>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  useEscapeKey(() => setShowDeleteDialog(false), showDeleteDialog);

  const templatesQuery = useQuery<SystemReasonTemplateDto[]>({
    queryKey: ['itil', 'reason-templates'],
    queryFn: async () => {
      try {
        const response = await apiClient.getSystemReasonTemplates();
        if (!Array.isArray(response)) {
          throw new Error('Reason templates response is not an array.');
        }
        return response.map((template, index) => normalizeTemplate(template, index));
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to load reason templates.'));
      }
    },
  });

  const templates = Array.isArray(templatesQuery.data) ? templatesQuery.data : [];
  const isEditingNew = selectedTemplateKey === NEW_TEMPLATE_KEY;
  const requestedTemplateId =
    selectedTemplateKey && selectedTemplateKey !== NEW_TEMPLATE_KEY ? selectedTemplateKey : null;
  const selectedExistingTemplateId =
    requestedTemplateId && templates.some((template) => template.id === requestedTemplateId)
      ? requestedTemplateId
      : templates[0]?.id ?? null;
  const selectedTemplate = selectedExistingTemplateId
    ? templates.find((template) => template.id === selectedExistingTemplateId) ?? null
    : null;

  const activeDraft = useMemo(() => {
    if (isEditingNew) {
      return newTemplateDraft;
    }

    if (!selectedTemplate) {
      return null;
    }

    return draftsByTemplateId[selectedTemplate.id] ?? toDraft(selectedTemplate);
  }, [draftsByTemplateId, isEditingNew, newTemplateDraft, selectedTemplate]);

  const upsertMutation = useMutation({
    mutationFn: async (draft: ReasonTemplateDraft) => {
      try {
        const payload: UpsertSystemReasonTemplateRequest = {
          name: draft.name.trim(),
          description: draft.description.trim() || undefined,
          isDefault: draft.isDefault,
          resolveReasons: toSortedReasons(draft.resolveReasons).map((reason) => ({
            label: reason.label.trim(),
            value: reason.value.trim(),
            description: reason.description?.trim() || undefined,
            isActive: reason.isActive,
            sortOrder: reason.sortOrder,
          })),
          onHoldReasons: toSortedReasons(draft.onHoldReasons).map((reason) => ({
            label: reason.label.trim(),
            value: reason.value.trim(),
            description: reason.description?.trim() || undefined,
            isActive: reason.isActive,
            sortOrder: reason.sortOrder,
            requiresFollowUp: reason.requiresFollowUp,
          })),
          cancelReasons: toSortedReasons(draft.cancelReasons).map((reason) => ({
            label: reason.label.trim(),
            value: reason.value.trim(),
            description: reason.description?.trim() || undefined,
            isActive: reason.isActive,
            sortOrder: reason.sortOrder,
          })),
        };

        const templateId = isEditingNew ? undefined : selectedTemplate?.id;
        return await apiClient.upsertSystemReasonTemplate(payload, templateId);
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to save reason template.'));
      }
    },
    onSuccess: async (savedTemplate) => {
      await queryClient.invalidateQueries({ queryKey: ['itil', 'reason-templates'] });
      addToast('Reason template saved successfully.', 'success');
      if (isEditingNew) {
        setNewTemplateDraft(null);
      }
      const savedTemplateId = savedTemplate?.id ?? null;
      setSelectedTemplateKey(savedTemplateId);
      setDraftsByTemplateId((current) => {
        if (!savedTemplateId) {
          return current;
        }
        const next = { ...current };
        delete next[savedTemplateId];
        return next;
      });
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Failed to save reason template.'), 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (templateId: string) => {
      try {
        await apiClient.deleteSystemReasonTemplate(templateId);
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to delete reason template.'));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['itil', 'reason-templates'] });
      addToast('Reason template deleted.', 'success');
      setShowDeleteDialog(false);
      setSelectedTemplateKey(null);
      setDraftsByTemplateId({});
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Failed to delete reason template.'), 'error');
    },
  });

  const updateActiveDraft = (updater: (draft: ReasonTemplateDraft) => ReasonTemplateDraft) => {
    if (isEditingNew) {
      setNewTemplateDraft((current) => (current ? updater(current) : current));
      return;
    }

    if (!selectedTemplate) {
      return;
    }

    setDraftsByTemplateId((current) => ({
      ...current,
      [selectedTemplate.id]: updater(current[selectedTemplate.id] ?? toDraft(selectedTemplate)),
    }));
  };

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.isDefault ? `${template.name} (Default)` : template.name,
  }));
  if (newTemplateDraft) {
    templateOptions.push({
      value: NEW_TEMPLATE_KEY,
      label: newTemplateDraft.name.trim() || 'New template (unsaved)',
    });
  }

  const selectedValue = isEditingNew ? NEW_TEMPLATE_KEY : selectedTemplate?.id ?? '';

  const currentReasons = useMemo(() => {
    if (!activeDraft) {
      return {
        items: [] as Array<EditableReason | EditableOnHoldReason>,
        key: 'resolveReasons' as const,
      };
    }

    if (activeSubTab === 'resolve') {
      return { items: toSortedReasons(activeDraft.resolveReasons), key: 'resolveReasons' as const };
    }

    if (activeSubTab === 'on-hold') {
      return { items: toSortedReasons(activeDraft.onHoldReasons), key: 'onHoldReasons' as const };
    }

    return { items: toSortedReasons(activeDraft.cancelReasons), key: 'cancelReasons' as const };
  }, [activeDraft, activeSubTab]);

  const addReason = () => {
    if (!activeDraft || currentReasons.items.length >= MAX_REASONS) {
      return;
    }

    updateActiveDraft((draft) => {
      if (activeSubTab === 'resolve') {
        return {
          ...draft,
          resolveReasons: [...draft.resolveReasons, createReason(draft.resolveReasons.length)],
        };
      }

      if (activeSubTab === 'on-hold') {
        return {
          ...draft,
          onHoldReasons: [...draft.onHoldReasons, createOnHoldReason(draft.onHoldReasons.length)],
        };
      }

      return {
        ...draft,
        cancelReasons: [...draft.cancelReasons, createReason(draft.cancelReasons.length)],
      };
    });
  };

  const validateDraft = (draft: ReasonTemplateDraft): string | null => {
    const name = draft.name.trim();
    if (!name) {
      return 'Template name is required.';
    }

    const hasDuplicateTemplateName = templates.some(
      (template) =>
        template.name.trim().toLowerCase() === name.toLowerCase() &&
        (isEditingNew || template.id !== selectedTemplate?.id),
    );
    if (hasDuplicateTemplateName) {
      return 'Template name must be unique.';
    }

    const groups: Array<{
      title: string;
      reasons: Array<EditableReason | EditableOnHoldReason>;
    }> = [
      { title: 'Resolve reasons', reasons: draft.resolveReasons },
      { title: 'On-hold reasons', reasons: draft.onHoldReasons },
      { title: 'Cancel reasons', reasons: draft.cancelReasons },
    ];

    for (const group of groups) {
      if (group.reasons.length > MAX_REASONS) {
        return `${group.title} cannot exceed ${MAX_REASONS}.`;
      }

      const values = new Set<string>();
      for (const reason of group.reasons) {
        const label = reason.label.trim();
        const value = reason.value.trim();

        if (!label) {
          return `${group.title} require labels.`;
        }

        if (!value) {
          return `${group.title} require auto-generated values.`;
        }

        const key = value.toLowerCase();
        if (values.has(key)) {
          return `${group.title} values must be unique.`;
        }
        values.add(key);
      }
    }

    return null;
  };

  const handleSave = () => {
    if (!activeDraft) {
      return;
    }

    const validationError = validateDraft(activeDraft);
    if (validationError) {
      addToast(validationError, 'error');
      return;
    }

    upsertMutation.mutate(activeDraft);
  };

  const isLoading = templatesQuery.isLoading;
  const isError = templatesQuery.isError;

  if (isLoading) {
    return (
      <div className="card">
        <p className="text-sm text-gray-600 dark:text-gray-300">Loading reason templates…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card rounded border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        Unable to load reason templates. {errorMessage(templatesQuery.error, '')}
      </div>
    );
  }

  if (!activeDraft && templates.length === 0) {
    return (
      <EmptyState
        title="No reason templates"
        message="Create your first reason configuration template."
        action={
          <button
            type="button"
            onClick={() => {
              setNewTemplateDraft(createDefaultDraft());
              setSelectedTemplateKey(NEW_TEMPLATE_KEY);
            }}
            className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700"
          >
            Create New Template
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <section className="card space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <Select
              id="reason-template-select"
              label="Template"
              value={selectedValue}
              onChange={setSelectedTemplateKey}
              options={templateOptions}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setNewTemplateDraft(createDefaultDraft());
              setSelectedTemplateKey(NEW_TEMPLATE_KEY);
            }}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Create New Template
          </button>
        </div>
      </section>

      {activeDraft ? (
        <section className="card space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="reason-template-name">
                Name
              </label>
              <input
                id="reason-template-name"
                type="text"
                value={activeDraft.name}
                onChange={(event) => updateActiveDraft((draft) => ({ ...draft, name: event.target.value }))}
                className="input"
              />
            </div>
            <div className="flex items-end">
              <Toggle
                id="reason-template-default"
                label="Set as Default"
                checked={activeDraft.isDefault}
                onChange={(checked) => updateActiveDraft((draft) => ({ ...draft, isDefault: checked }))}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="reason-template-description">
              Description
            </label>
            <textarea
              id="reason-template-description"
              value={activeDraft.description}
              onChange={(event) => updateActiveDraft((draft) => ({ ...draft, description: event.target.value }))}
              className="input min-h-[88px]"
            />
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'resolve', label: 'Resolve Reasons' },
                { key: 'on-hold', label: 'On-Hold Reasons' },
                { key: 'cancel', label: 'Cancel Reasons' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveSubTab(tab.key as ReasonSubTab)}
                  className={`rounded-md px-4 py-2 text-sm font-medium ${
                    activeSubTab === tab.key
                      ? 'bg-primary-700 text-white dark:bg-primary-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {currentReasons.items.length} / {MAX_REASONS} reasons
              </p>
              <button
                type="button"
                onClick={addReason}
                disabled={currentReasons.items.length >= MAX_REASONS}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Add Reason
              </button>
            </div>

            <div className="space-y-3">
              {currentReasons.items.map((reason, index) => (
                <div key={reason.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                  <div className="grid gap-3 md:grid-cols-12">
                    <div className="md:col-span-4">
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Label</label>
                      <input
                        type="text"
                        value={reason.label}
                        onChange={(event) => {
                          const label = event.target.value;
                          const value = slugifyReasonValue(label);
                          updateActiveDraft((draft) => {
                            const items = [...draft[currentReasons.key]];
                            items[index] = { ...items[index], label, value };
                            return { ...draft, [currentReasons.key]: items };
                          });
                        }}
                        className="input"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Value</label>
                      <input type="text" value={reason.value} readOnly className="input opacity-80" />
                    </div>
                    <div className="md:col-span-5">
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Description</label>
                      <input
                        type="text"
                        value={reason.description ?? ''}
                        onChange={(event) => {
                          updateActiveDraft((draft) => {
                            const items = [...draft[currentReasons.key]];
                            items[index] = { ...items[index], description: event.target.value };
                            return { ...draft, [currentReasons.key]: items };
                          });
                        }}
                        className="input"
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-4">
                      <Toggle
                        id={`${currentReasons.key}-active-${reason.id}`}
                        label="Active"
                        checked={reason.isActive}
                        onChange={(checked) => {
                          updateActiveDraft((draft) => {
                            const items = [...draft[currentReasons.key]];
                            items[index] = { ...items[index], isActive: checked };
                            return { ...draft, [currentReasons.key]: items };
                          });
                        }}
                      />
                      {activeSubTab === 'on-hold' ? (
                        <Toggle
                          id={`${currentReasons.key}-follow-up-${reason.id}`}
                          label="Requires Follow-Up"
                          checked={Boolean((reason as EditableOnHoldReason)?.requiresFollowUp)}
                          onChange={(checked) => {
                            updateActiveDraft((draft) => {
                              const items = [...draft.onHoldReasons];
                              items[index] = { ...items[index], requiresFollowUp: checked };
                              return { ...draft, onHoldReasons: items };
                            });
                          }}
                        />
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          updateActiveDraft((draft) => ({
                            ...draft,
                            [currentReasons.key]: moveReason(draft[currentReasons.key], index, index - 1),
                          }))
                        }
                        disabled={index === 0}
                        className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
                        aria-label="Move reason up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updateActiveDraft((draft) => ({
                            ...draft,
                            [currentReasons.key]: moveReason(draft[currentReasons.key], index, index + 1),
                          }))
                        }
                        disabled={index === currentReasons.items.length - 1}
                        className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
                        aria-label="Move reason down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updateActiveDraft((draft) => ({
                            ...draft,
                            [currentReasons.key]: toSortedReasons(
                              draft[currentReasons.key].filter((item) => item.id !== reason.id),
                            ),
                          }))
                        }
                        className="rounded border border-red-300 px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {currentReasons.items.length === 0 ? (
                <div className="rounded border border-dashed border-gray-300 p-4 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300">
                  No reasons configured for this category.
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={upsertMutation.isPending}
              className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-primary-600 dark:hover:bg-primary-700"
            >
              {upsertMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {!isEditingNew && selectedTemplate ? (
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
              >
                Delete
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          if (selectedTemplate) {
            deleteMutation.mutate(selectedTemplate.id);
          }
        }}
        title="Delete template?"
        message="This will permanently remove the selected reason template."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
